import { useEffect, useState } from "react";
import { appConfirm } from "../../../components/ConfirmDialog";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Check, X, Send, FileText, Zap } from "lucide-react";
import { money } from "./shared";

const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const snapOf = (r) => JSON.stringify({ description: r.description, qty: String(r.qty), unit_price: String(r.unit_price), disc_type: r.disc_type, disc_value: String(r.disc_value) });

const toRow = (it) => {
  const r = {
    id: it.id,
    description: it.description || "",
    qty: it.qty ?? 1,
    unit_price: it.unit_price ?? it.amount ?? 0,
    disc_type: it.discount?.type || "none",
    disc_value: it.discount?.value ?? "",
    s_status: it.discount_status || "none",
    s_amount: it.amount ?? 0,
    requested_by: it.requested_by || null,
    authorised_by: it.authorised_by || null,
  };
  r._snap = snapOf(r);
  return r;
};

export function InvoiceEditor({ data, role, reference, reload }) {
  const b = data.booking;
  const ceiling = Number(data.auto_approve_ceiling_pct || 0);
  const isAdmin = role === "admin" || role === "senior_admin";
  const [rows, setRows] = useState((b.custom_line_items || []).map(toRow));
  const [dirty, setDirty] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { setRows((b.custom_line_items || []).map(toRow)); setDirty(false); }, [b.custom_line_items]);

  const calc = (r) => {
    const qty = parseFloat(r.qty) || 0;
    const up = parseFloat(r.unit_price) || 0;
    const subtotal = r2(qty * up);
    const dv = parseFloat(r.disc_value) || 0;
    const hasDisc = r.disc_type !== "none" && dv > 0;
    let discAmt = 0, effPct = 0;
    if (hasDisc) {
      discAmt = r.disc_type === "pct" ? r2(subtotal * dv / 100) : r2(Math.min(dv, subtotal));
      effPct = subtotal > 0 ? r2(discAmt / subtotal * 100) : 0;
    }
    const willPend = hasDisc && !isAdmin && effPct > ceiling;
    const net = willPend ? subtotal : r2(subtotal - discAmt);
    return { subtotal, discAmt, effPct, hasDisc, willPend, net };
  };
  const changed = (r) => r._new || snapOf(r) !== r._snap;

  const savedCustomSum = (b.custom_line_items || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const baseTotal = r2((Number(b.total_inc_vat) || 0) - savedCustomSum);
  const previewCustomSum = r2(rows.reduce((s, r) => s + calc(r).net, 0));
  const previewTotal = r2(baseTotal + previewCustomSum);
  const previewPending = rows.filter(r => calc(r).willPend).length;
  const savedPending = rows.filter(r => !changed(r) && r.s_status === "pending").length;

  const upd = (id, k, v) => { setRows(rs => rs.map(r => r.id === id ? { ...r, [k]: v } : r)); setDirty(true); };
  const addRow = () => {
    const id = window.crypto?.randomUUID?.() || String(Date.now());
    setRows(rs => [...rs, { id, description: "", qty: 1, unit_price: "", disc_type: "none", disc_value: "", s_status: "none", _new: true, _snap: "" }]);
    setDirty(true);
  };
  const removeRow = (id) => { setRows(rs => rs.filter(r => r.id !== id)); setDirty(true); };

  const save = async () => {
    const items = rows.map(r => ({
      id: r.id,
      description: (r.description || "").trim(),
      qty: parseFloat(r.qty) || 1,
      unit_price: parseFloat(r.unit_price) || 0,
      discount: r.disc_type !== "none" && parseFloat(r.disc_value) > 0 ? { type: r.disc_type, value: parseFloat(r.disc_value) } : null,
    }));
    if (items.some(i => !i.description)) { toast.error("Each line needs a description"); return; }
    try {
      const res = await api.put(`/admin/booking/${reference}/line-items`, { items });
      const pc = res.data.pending_count || 0;
      toast.success(`Invoice saved — total ${money(res.data.total_inc_vat)}${pc ? ` · ${pc} discount(s) awaiting authorisation` : ""}${res.data.invoice_regenerated ? " · PDF updated" : ""}`);
      setDirty(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save invoice"); }
  };
  const authorise = async (id, action) => {
    try {
      const res = await api.post(`/admin/booking/${reference}/line-items/${id}/authorise`, { action });
      toast.success(action === "approve" ? `Discount approved — total ${money(res.data.total_inc_vat)}` : "Discount rejected");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update authorisation"); }
  };
  const sendInvoice = async () => {
    if (dirty && !(await appConfirm({ title: "Unsaved changes", description: "You have unsaved line-item changes. Send the invoice using the last saved version?", confirmLabel: "Send last saved" }))) return;
    if (!(await appConfirm({ title: "Email the invoice?", description: `Email the invoice PDF to ${b.email || "the client"}?`, confirmLabel: "Send invoice" }))) return;
    setSending(true);
    try {
      const res = await api.post(`/admin/booking/${reference}/invoice/send`);
      toast.success(res.data.sent ? `Invoice emailed to ${res.data.to}` : "Invoice generated, but the email could not be sent");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send invoice"); }
    setSending(false);
  };

  const Badge = ({ tone, children, testid }) => {
    const tones = {
      green: "bg-[#E7F4EC] text-[#1F6B3B] border-[#B7DEC6]",
      amber: "bg-[#FBF3EC] text-[#9A6B12] border-[#EAC7B4]",
      grey: "bg-[#F1F1F0] text-[#6B7280] border-[#E5E7EB]",
    };
    return <span data-testid={testid} className={`text-[10px] font-bold uppercase tracking-wide border px-1.5 py-0.5 ${tones[tone]}`}>{children}</span>;
  };

  return (
    <div className="border border-[#E5E7EB] bg-white p-4 mb-6" data-testid="invoice-editor">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-bold flex items-center gap-2"><FileText size={16} /> Invoice line items</div>
        <button data-testid="invoice-send" onClick={sendInvoice} disabled={sending} className="de-btn-secondary text-xs flex items-center gap-1 disabled:opacity-50" title="Emails the invoice PDF to the client">
          <Send size={13} /> {sending ? "Sending…" : "Send to client"} <Zap size={10} className="fill-[#F59E0B] text-[#B45309]" />
        </button>
      </div>
      <p className="text-xs text-[#6B7280] mb-3">
        Add extra works with quantity, unit price and an optional per-line discount. {isAdmin
          ? "Your discounts apply immediately."
          : <>Discounts above <b data-testid="invoice-ceiling">{ceiling}%</b> are saved for an administrator to authorise before they affect the client total.</>}
      </p>

      {savedPending > 0 && (
        <div className="border border-[#EAC7B4] bg-[#FBF3EC] p-2 mb-3 text-xs text-[#9A6B12]" data-testid="invoice-pending-banner">
          {savedPending} discount{savedPending > 1 ? "s" : ""} awaiting authorisation{isAdmin ? " — approve or reject below." : " by an administrator."}
        </div>
      )}

      <div className="space-y-2 mb-3">
        {rows.map((r, idx) => {
          const c = calc(r);
          const dirtyRow = changed(r);
          return (
            <div key={r.id} data-testid={`invoice-line-${idx}`} className="border border-[#E5E7EB] p-3 space-y-2">
              <div className="flex items-start gap-2">
                <input data-testid={`invoice-line-desc-${idx}`} value={r.description} onChange={e => upd(r.id, "description", e.target.value)}
                  placeholder="Description (e.g. Additional patio doors)" className="border border-[#E5E7EB] px-3 py-2 text-sm flex-1" />
                <button data-testid={`invoice-line-remove-${idx}`} onClick={() => removeRow(r.id)} className="text-[#C79A3B] mt-2 shrink-0" aria-label="Remove line item"><Trash2 size={15} /></button>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <label className="flex items-center gap-1 text-xs text-[#6B7280]">Qty
                  <input data-testid={`invoice-line-qty-${idx}`} type="number" value={r.qty} onChange={e => upd(r.id, "qty", e.target.value)} className="border border-[#E5E7EB] px-2 py-1 text-sm w-16" />
                </label>
                <label className="flex items-center gap-1 text-xs text-[#6B7280]">Unit £
                  <input data-testid={`invoice-line-price-${idx}`} type="number" value={r.unit_price} onChange={e => upd(r.id, "unit_price", e.target.value)} placeholder="0" className="border border-[#E5E7EB] px-2 py-1 text-sm w-24" />
                </label>
                <select data-testid={`invoice-line-disctype-${idx}`} value={r.disc_type} onChange={e => upd(r.id, "disc_type", e.target.value)} className="border border-[#E5E7EB] px-2 py-1 text-sm bg-white">
                  <option value="none">No discount</option>
                  <option value="pct">% off</option>
                  <option value="gbp">£ off</option>
                </select>
                {r.disc_type !== "none" && (
                  <input data-testid={`invoice-line-discvalue-${idx}`} type="number" value={r.disc_value} onChange={e => upd(r.id, "disc_value", e.target.value)}
                    placeholder={r.disc_type === "pct" ? "%" : "£"} className="border border-[#E5E7EB] px-2 py-1 text-sm w-20" />
                )}
                <div className="ml-auto text-right">
                  <div className="font-bold tabular-nums" data-testid={`invoice-line-net-${idx}`}>{money(c.net)}</div>
                  {c.hasDisc && !c.willPend && <div className="text-[10px] text-[#6B7280] line-through tabular-nums">{money(c.subtotal)}</div>}
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 min-h-[18px]">
                <div className="flex items-center gap-2">
                  {dirtyRow ? (
                    c.hasDisc && (c.willPend
                      ? <Badge tone="amber" testid={`invoice-line-status-${idx}`}>Will need authorisation</Badge>
                      : <Badge tone="green" testid={`invoice-line-status-${idx}`}>Discount applies</Badge>)
                  ) : (
                    <>
                      {r.s_status === "applied" && <Badge tone="green" testid={`invoice-line-status-${idx}`}>Discount applied</Badge>}
                      {r.s_status === "pending" && <Badge tone="amber" testid={`invoice-line-status-${idx}`}>Awaiting authorisation</Badge>}
                      {r.s_status === "rejected" && <Badge tone="grey" testid={`invoice-line-status-${idx}`}>Discount rejected</Badge>}
                    </>
                  )}
                  {!dirtyRow && r.s_status === "pending" && r.requested_by && <span className="text-[10px] text-[#6B7280]">requested by {r.requested_by}</span>}
                  {!dirtyRow && r.s_status === "applied" && r.authorised_by && r.disc_type !== "none" && <span className="text-[10px] text-[#6B7280]">by {r.authorised_by}</span>}
                </div>
                {!dirtyRow && r.s_status === "pending" && isAdmin && (
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`invoice-line-approve-${idx}`} onClick={() => authorise(r.id, "approve")} className="text-xs flex items-center gap-1 border border-[#B7DEC6] bg-[#E7F4EC] text-[#1F6B3B] px-2 py-1 font-bold"><Check size={12} /> Approve</button>
                    <button data-testid={`invoice-line-reject-${idx}`} onClick={() => authorise(r.id, "reject")} className="text-xs flex items-center gap-1 border border-[#EAC7B4] bg-[#FBF3EC] text-[#9A6B12] px-2 py-1 font-bold"><X size={12} /> Reject</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {rows.length === 0 && <div className="text-sm text-[#6B7280]">No extra line items yet.</div>}
      </div>

      <button data-testid="invoice-line-add" onClick={addRow} className="de-btn-secondary text-sm flex items-center gap-1 mb-3"><Plus size={14} /> Add line item</button>

      <div className="border-t border-[#E5E7EB] pt-3 flex items-center justify-between gap-3">
        <div className="text-xs text-[#6B7280]">
          Line items: <b className="tabular-nums">{money(previewCustomSum)}</b> · Contract total: <b className="tabular-nums" data-testid="invoice-editor-total">{money(previewTotal)}</b>
          {previewPending > 0 && <span className="text-[#9A6B12]"> · {previewPending} pending discount{previewPending > 1 ? "s" : ""} not yet applied</span>}
        </div>
        <button data-testid="invoice-save" onClick={save} disabled={!dirty} className={`de-btn-primary text-sm ${!dirty ? "opacity-40 cursor-not-allowed" : ""}`}>Save &amp; update invoice</button>
      </div>
    </div>
  );
}
